<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { computed, reactive, ref } from 'vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { Search } from '@element-plus/icons-vue'
import type { IServiceFilters } from '@/types/rc/filters'
import type { IService } from '@/types/rc/work-orders'
import { currencyFormatter } from '@/helpers/ep/formatters'
import { useDebounceFn } from '@vueuse/core'
import ServiceDialog from '@/components/rc/services/ServiceDialog.vue'
import { useServices } from '@/composables/rc/services'

// DELETE mutation
const { deleteServiceMutation } = useServices()

const searchQuery = ref<string>()
const upsertDialogVisible = ref<boolean>(false)
const serviceBeingModified = ref<IService>()

const filters = reactive<IServiceFilters>({
  fragment: undefined,
})

const { getServicesQuery } = useServices()

const { isFetching, isError, isRefetching, data, error, refetch } = getServicesQuery(
  filters,
  computed(() => true)
)

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

/**
 * Handles edit
 *
 * @param service service to edit.
 */
const handleEdit = async (service: IService) => {
  serviceBeingModified.value = service
  upsertDialogVisible.value = true
}

const debouncedSearch = useDebounceFn((query: string) => {
  filters.fragment = query
}, 500)
</script>

<template>
  <ViewLayout class="services">
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isFetching" @click="upsertDialogVisible = true">Create Service</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="services__filters">
        <el-input
          v-model="searchQuery"
          style="width: 232px"
          placeholder="Description"
          clearable
          :suffix-icon="Search"
          @update:model-value="debouncedSearch"
        />
      </div>
      <el-table v-loading="isFetching" :data="data" :empty-text="tableEmptyText" stripe>
        <el-table-column prop="id" label="ID" :min-width="32" />
        <el-table-column prop="icon" label="Icon" :min-width="32">
          <template #default="scope">
            <Icon :icon="scope.row.icon" inline height="20" />
          </template>
        </el-table-column>
        <el-table-column prop="description" label="Description" :min-width="160" />
        <el-table-column prop="price" label="Price " :min-width="160" :formatter="currencyFormatter" />
        <el-table-column :min-width="70" align="right">
          <template #default="scope">
            <el-button link title="Edit" @click="handleEdit(scope.row as IService)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Service',
                  'Are you sure you want to delete this service? This action cannot be undone',
                  scope.row.id,
                  deleteServiceMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>
    <ServiceDialog
      v-model="upsertDialogVisible"
      :service-to-update="serviceBeingModified"
      @close="serviceBeingModified = undefined"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.services {
  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }
}
</style>
